import React, {useCallback, useEffect, useState} from "react";
import {apiV2} from "../../api/axiosConfig";
import {isAxiosError} from "axios";
import {Link, useNavigate, useSearchParams} from "react-router-dom";
import {FacilityForm, FacilityFormData} from "./facility-form";

export const NewFacility: React.FC = () => {
    const navigate = useNavigate();
    const [searchParams] = useSearchParams();
    const prevUrl = searchParams.get("prev") || "/facilities";

    const [saving, setSaving] = useState<boolean>(false);
    const [saved, setSaved] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");

    const [errorTimeout, setErrorTimeout] = useState<NodeJS.Timeout>();
    const [savedTimeout, setSavedTimeout] = useState<NodeJS.Timeout>();

    const handleSave = useCallback(async (formData: FacilityFormData) => {
        if (!formData.name) {
            setErrorMessage("Facility name is required");
            return;
        }

        setSaved(false);
        setSaving(true);

        try {
            // Build address lines array (filter out empty ones)
            const addressLines = [
                formData.addressLine1,
                formData.addressLine2,
                formData.addressLine3
            ].filter(line => line.trim() !== "");

            const payload = {
                clientID: formData.clientID,
                name: formData.name,
                address: {
                    countryISO: formData.countryISO || "",
                    region: formData.region,
                    county: formData.county,
                    town: formData.town,
                    postalCode: formData.postalCode,
                    addressLines,
                },
                departmentID: formData.departmentID,
                latitude: formData.latitude,
                longitude: formData.longitude,
                guardianIDs: formData.guardianIDs
            };

            await apiV2.post("facilities", payload);

            setSaved(true);

            // Navigate back to facility list after successful creation
            setTimeout(() => {
                navigate(prevUrl);
            }, 1500);

        } catch (e) {
            if (isAxiosError(e) && e?.response?.data?.error) {
                setErrorMessage(e.response.data.error);
            } else {
                setErrorMessage("An error occurred while creating the facility");
            }
        }
        setSaving(false);
    }, [navigate]);

    useEffect(() => {
        if (errorMessage && errorTimeout) {
            clearTimeout(errorTimeout);
        }

        const timeout = setTimeout(() => {
            setErrorMessage("");
        }, 5000);

        setErrorTimeout(timeout);
    }, [errorMessage]);

    useEffect(() => {
        if (savedTimeout) {
            clearTimeout(savedTimeout);
        }

        const timeout = setTimeout(() => {
            setSaved(false);
        }, 5000);

        setSavedTimeout(timeout);
    }, [saved]);

    return (
        <div className="flex flex-col gap-8">
            <nav className="flex items-center gap-2 text-sm text-light-grey">
                <Link to="/sites" className="hover:text-grey">Sites</Link>
                <span>&gt;</span>
                <Link to={prevUrl} className="hover:text-grey">Facilities</Link>
                <span>&gt;</span>
                <span className="text-grey">Create Facility</span>
            </nav>
            <div className="bg-white shadow-md p-6 w-full max-w-2xl flex flex-col gap-6">
                <h1 className="text-2xl font-semibold">Create New Facility</h1>

                <FacilityForm
                    mode="create"
                    onSave={handleSave}
                    saving={saving}
                    saved={saved}
                    errorMessage={errorMessage}
                />
            </div>
        </div>
    );
};
